import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import CustomButton from "../../pages/CustomButton";
import { BASE_URL } from "../../config";
import { getAuthHeaders } from "../../services/useAuth";
import ProductSalesModal from "../coordinator/Cosmacia/ProductSalesModal";
import formatDate from "../../utils/formatDate";
import Loader from "../../dist/Loader";
import XLSX from "xlsx-js-style";

const MonthlyCountCampaign = () => {
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [tableData, setTableData] = useState([]);
  const [showContainers, setShowContainers] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [schemeOn, setSchemeOn] = useState("");

  const [totals, setTotals] = useState({ totalCount: 0, totalEmployees: 0 });

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/states`, {
          headers: getAuthHeaders(),
        });
        setStates(res.data || []);
      } catch (err) {
        console.error(err);
        setError("Failed to load states");
      }
    };
    fetchStates();
  }, []);

  useEffect(() => {
    if (!selectedState) {
      setDivisions([]);
      setSelectedDivision("All");
      return;
    }
    const fetchDivisions = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/divisions?state_id=${selectedState}`,
          { headers: getAuthHeaders() },
        );
        setDivisions([{ id: "All", name: "All" }, ...(res.data || [])]);
        setSelectedDivision("All");
      } catch (err) {
        console.error(err);
        setError("Failed to load divisions");
      }
    };
    fetchDivisions();
  }, [selectedState]);

  const handleGoClick = async () => {
    if (!selectedState) {
      setError("Please select a state");
      return;
    }

    setIsLoading(true);
    setError(null);
    setTableData([]);
    setTotals({ totalCount: 0, totalEmployees: 0 });

    try {
      let url = `${BASE_URL}/api/reports/employee-temptation-pob-report-monthly?state_id=${selectedState}`;

      // Division
      if (selectedDivision && selectedDivision !== "All") {
        const divisionName = divisions.find(
          (d) => String(d.id) === String(selectedDivision),
        )?.name;

        if (divisionName) {
          url += `&division=${encodeURIComponent(divisionName)}`;
        }
      }

      // Year
      if (selectedYear) {
        url += `&year=${selectedYear}`;
      }

      // Month
      if (selectedMonth) {
        url += `&month=${String(selectedMonth).padStart(2, "0")}`;
      }

      // ✅ Scheme On (NEW)
      if (schemeOn && Number(schemeOn) > 0) {
        url += `&scheme_on=${schemeOn}`;
        // OR if backend expects scheme_id
        // url += `&scheme_id=${schemeOn}`;
      }

      const res = await axios.get(url, { headers: getAuthHeaders() });

      const payload = res.data || {};
      let rows = [];

      if (Array.isArray(payload.data)) rows = payload.data;
      else if (Array.isArray(payload.employees)) rows = payload.employees;
      else if (payload.data?.employees) rows = payload.data.employees;
      else if (Array.isArray(payload)) rows = payload;

      setTableData(rows || []);
      setTotals({
        totalCount: payload.count || rows.length || 0,
        totalEmployees: payload.employees_count || rows.length || 0,
      });

      setShowContainers(true);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch data");
    } finally {
      setIsLoading(false);
    }
  };

  const months = [
    { value: 1, label: "Jan" },
    { value: 2, label: "Feb" },
    { value: 3, label: "Mar" },
    { value: 4, label: "Apr" },
    { value: 5, label: "May" },
    { value: 6, label: "Jun" },
    { value: 7, label: "Jul" },
    { value: 8, label: "Aug" },
    { value: 9, label: "Sep" },
    { value: 10, label: "Oct" },
    { value: 11, label: "Nov" },
    { value: 12, label: "Dec" },
  ];
  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i,
  );

  return (
    <div className="p-2  border-2 border-gray-300 rounded-lg bg-white">
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25">
          <Loader onCancel={() => setIsLoading(false)} />
        </div>
      )}
      <div className="bg-white border rounded-xl p-6">
        <h2 className="text-xl font-bold text-center text-blue-600 mb-8">
          FF Wise Temptation
        </h2>

        {/* ================= ROW 1 ================= */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
          {/* State */}
          <div className="flex items-center gap-4">
            <label className="w-24 text-sm font-medium text-gray-700 text-right">
              State
            </label>
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="flex-1 border rounded px-3 py-2 text-sm"
            >
              <option value="">Select State</option>
              {states.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          {/* Division */}
          <div className="flex items-center gap-4">
            <label className="w-24 text-sm font-medium text-gray-700 text-right">
              Division
            </label>
            <select
              value={selectedDivision}
              onChange={(e) => setSelectedDivision(e.target.value)}
              disabled={!selectedState}
              className="flex-1 border rounded px-3 py-2 text-sm disabled:bg-gray-100"
            >
              <option value="">Select Division</option>
              {divisions.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ================= ROW 2 ================= */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {/* LEFT: Year + Month */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Year */}
            <div className="flex items-center gap-4">
              <label className="w-24 text-sm font-medium text-gray-700 text-right">
                Year
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="flex-1 border rounded px-3 py-2 text-sm"
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            {/* Month */}
            <div className="flex items-center gap-4">
              <label className="w-24 text-sm font-medium text-gray-700 text-right">
                Month
              </label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="flex-1 border rounded px-3 py-2 text-sm"
              >
                {months.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* RIGHT: Scheme On */}
          <div className="flex items-center gap-4">
            <label className="w-24 text-sm font-medium text-gray-700 text-right">
              Scheme On
            </label>
            <input
              type="number"
              min="0"
              value={schemeOn}
              onChange={(e) => setSchemeOn(e.target.value)}
              placeholder="0"
              className="flex-1 border rounded px-3 py-2 text-sm"
            />
          </div>
        </div>

        {/* SUBMIT BUTTON */}
        <div className="flex justify-center mt-4">
          <CustomButton
            variant="go"
            onClick={handleGoClick}
            disabled={isLoading}
          >
            {isLoading ? "Loading..." : "GO"}
          </CustomButton>
        </div>
      </div>

      {showContainers && (
        <div className="responsive-table-container mt-4">
          <div className="table-scroll-wrapper border rounded p-2 bg-white">
            {isLoading ? (
              <div className="text-center p-4">Loading data...</div>
            ) : tableData.length > 0 ? (
              <MonthlyAttendanceTable
                data={tableData}
                month={selectedMonth}
                year={selectedYear}
              />
            ) : (
              <div className="text-center p-4">No data available</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const MonthlyAttendanceTable = ({ data, month, year }) => {
  const tableRef = useRef(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getDayOfWeek = (year, month, day) => {
    const date = new Date(year, month - 1, day);
    return date.getDay();
  };

  const daysInMonth = new Date(year, month, 0).getDate();

  const colWidths = {
    serialNo: "40px",
    empId: "80px",
    state: "120px",
    substate: "120px",
    employee: "180px",
    sms: "100px",
    doj: "90px",
    dor: "90px",
    manager: "110px",
    designation: "140px",
    city: "120px",
    division: "120px",
    day: "30px",
    total: "60px",
  };

  const fixedColsWidth =
    parseInt(colWidths.serialNo) +
    parseInt(colWidths.empId) +
    parseInt(colWidths.state) +
    parseInt(colWidths.employee) +
    parseInt(colWidths.sms) +
    parseInt(colWidths.doj) +
    parseInt(colWidths.dor) +
    parseInt(colWidths.manager) +
    parseInt(colWidths.designation) +
    parseInt(colWidths.city) +
    parseInt(colWidths.division) +
    parseInt(colWidths.total);

  const daysWidth = daysInMonth * parseInt(colWidths.day);
  const minTableWidth = Math.max(900, fixedColsWidth + daysWidth);

  const handleDayClick = (employeeId, day) => {
    const d = new Date(year, month - 1, day);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const dateStr = `${yyyy}-${mm}-${dd}`;

    setSelectedEmployee(employeeId);
    setSelectedDate(dateStr);
  };

  const closeModal = () => {
    setSelectedEmployee(null);
    setSelectedDate(null);
  };

  const generateDayHeaders = () => {
    const headers = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const dayOfWeek = getDayOfWeek(year, month, day);
      const isSunday = dayOfWeek === 0;
      headers.push(
        <th
          key={day}
          align="center"
          scope="col"
          style={{
            backgroundColor: isSunday ? "LightGreen" : "#0B69A3",
            color: "#ffffff",
            width: colWidths.day,
            padding: "4px 2px",
            fontSize: "11px",
            whiteSpace: "nowrap",
            position: "sticky",
            top: 0,
            zIndex: 4,
            borderLeft: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <div style={{ lineHeight: "12px", fontWeight: 700 }}>{day}</div>
          <div style={{ fontSize: 9, lineHeight: "10px", opacity: 0.95 }}>
            {weekdayLabels[dayOfWeek]}
          </div>
        </th>,
      );
    }
    return headers;
  };

  const generateDayCells = (rowData) => {
    const cells = [];

    for (let day = 1; day <= daysInMonth; day++) {
      const dayOfWeek = getDayOfWeek(year, month, day);
      const isSunday = dayOfWeek === 0;

      let dayValue = 0;
      let dayPrice = 0;
      let dayPob = 0;

      if (rowData.days && rowData.days[String(day)]) {
        const dayObj = rowData.days[String(day)];
        dayValue = Number(dayObj.qty || 0);
        dayPrice = Number(dayObj.price || 0);
        dayPob = Number(dayObj.pob || 0);
      }

      // ✅ FINAL CONDITION
      const isPobLessThanPrice = dayPob < dayPrice && dayPob > 0;

      cells.push(
        <td
          key={day}
          align="center"
          style={{
            width: colWidths.day,
            padding: "2px",
            backgroundColor: isSunday ? "LightGreen" : "transparent",
            fontWeight: 700,
            fontSize: "12px",
            whiteSpace: "nowrap",
            cursor: dayValue > 0 ? "pointer" : "default",
          }}
          onClick={() => dayValue > 0 && handleDayClick(rowData.emp_id, day)}
          title={`Qty: ${dayValue} | Price: ${dayPrice} | POB: ${dayPob}`}
        >
          <a
            href="#"
            onClick={(e) => e.preventDefault()}
            className={
              dayValue > 0
                ? isPobLessThanPrice
                  ? "text-red-700 font-bold"
                  : "text-blue-700 font-bold"
                : "text-gray-400"
            }
          >
            {dayValue}
          </a>
        </td>,
      );
    }

    return cells;
  };

  const handlePrint = () => {
    if (!tableRef.current) return;

    const tableHTML = tableRef.current.outerHTML;

    const printWindow = window.open("", "", "height=800,width=1200");

    printWindow.document.write(`
    <html>
      <head>
        <title>Monthly Count Report</title>
       <style>
  @page {
    size: landscape;
  }

  * {
    box-sizing: border-box;
    font-family: Arial, sans-serif;
  }

  body {
    padding: 20px;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    font-size: 11px;
  }

  th, td {
    border: 1px solid #ccc;
    padding: 4px 6px;
    text-align: center;
  }

  thead th {
    background: #0B69A3;
    color: white;
  }
</style>
      </head>

      <body>
        ${tableHTML}
      </body>
    </html>
  `);

    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };

  const handleExportExcel = () => {
    if (!tableRef.current) return;

    const ws = XLSX.utils.table_to_sheet(tableRef.current);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MonthlyCount");

    XLSX.writeFile(wb, `monthly-count-${month}-${year}.xlsx`);
  };

  return (
    <>
      <div className="border rounded shadow  table-wrapper">
        <table ref={tableRef} className="unlock-dcr-leave-table text-sm ">
          <thead className="bg-blue-500 text-white">
            <tr style={{ background: "#cfeefb" }}>
              <th
                align="center"
                scope="col"
                style={{
                  padding: "6px",
                  fontSize: "13px",
                  width: colWidths.serialNo,
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  background: "#0B69A3",
                }}
              >
                S.No
              </th>

              <th
                align="center"
                scope="col"
                style={{
                  padding: "6px",
                  fontSize: "13px",
                  width: colWidths.empId,
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  background: "#0B69A3",
                }}
              >
                Emp ID
              </th>

              <th
                align="center"
                scope="col"
                style={{
                  padding: "6px",
                  fontSize: "13px",
                  width: colWidths.state,
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  background: "#0B69A3",
                }}
              >
                State
              </th>
              <th
                align="center"
                scope="col"
                style={{
                  padding: "6px",
                  fontSize: "13px",
                  width: colWidths.substate,
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  background: "#0B69A3",
                }}
              >
                Sub State
              </th>

              <th
                align="center"
                scope="col"
                style={{
                  padding: "6px",
                  fontSize: "13px",
                  width: colWidths.employee,
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  background: "#0B69A3",
                }}
              >
                Employee Name
              </th>
              <th
                align="center"
                scope="col"
                style={{
                  padding: "6px",
                  fontSize: "13px",
                  width: colWidths.designation,
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  background: "#0B69A3",
                }}
              >
                Designation
              </th>
              <th
                align="center"
                scope="col"
                style={{
                  padding: "6px",
                  fontSize: "13px",
                  width: colWidths.city,
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  background: "#0B69A3",
                }}
              >
                City/HQ
              </th>

              <th
                align="center"
                scope="col"
                style={{
                  padding: "6px",
                  fontSize: "13px",
                  width: colWidths.division,
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  background: "#0B69A3",
                }}
              >
                Division
              </th>
              <th
                align="center"
                scope="col"
                style={{
                  padding: "6px",
                  fontSize: "13px",
                  width: colWidths.doj,
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  background: "#0B69A3",
                }}
              >
                DOJ
              </th>

              <th
                align="center"
                scope="col"
                style={{
                  padding: "6px",
                  fontSize: "13px",
                  width: colWidths.dor,
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  background: "#0B69A3",
                }}
              >
                DOR
              </th>

              <th
                align="center"
                scope="col"
                style={{
                  padding: "6px",
                  fontSize: "13px",
                  width: colWidths.manager,
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  background: "#0B69A3",
                }}
              >
                Manager
              </th>
              <th
                align="center"
                scope="col"
                style={{
                  padding: "6px",
                  fontSize: "13px",
                  width: colWidths.sms,
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  background: "#0B69A3",
                }}
              >
                Mobile No
              </th>

              {generateDayHeaders()}

              <th
                align="center"
                scope="col"
                style={{
                  padding: "6px",
                  fontSize: "13px",
                  width: colWidths.total,
                  position: "sticky",
                  top: 0,
                  zIndex: 5,
                  background: "#0B69A3",
                }}
              >
                Total
              </th>
            </tr>
          </thead>

          <tbody>
            {data.map((row, index) => (
              <tr
                key={index}
                align="left"
                style={{
                  color: index % 2 === 0 ? "#333333" : "#284775",
                  backgroundColor: index % 2 === 0 ? "#F7F6F3" : "white",
                  fontSize: "13px",
                }}
              >
                <td
                  align="center"
                  style={{ padding: "6px", width: colWidths.serialNo }}
                >
                  {index + 1}
                </td>

                <td
                  align="left"
                  style={{ padding: "6px", width: colWidths.empId }}
                >
                  <span>{row.employee}</span>
                </td>

                <td
                  align="left"
                  style={{ padding: "6px", width: colWidths.state }}
                >
                  <span
                    title={row.state}
                    style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.state}
                  </span>
                </td>
                <td
                  align="left"
                  style={{ padding: "6px", width: colWidths.sub_state }}
                >
                  <span
                    title={row.sub_state}
                    style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.sub_state}
                  </span>
                </td>

                <td
                  align="left"
                  style={{ padding: "6px", width: colWidths.employee }}
                >
                  <span
                    title={row.employee_name}
                    style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.employee_name}
                  </span>
                </td>
                <td
                  align="left"
                  style={{ padding: "6px", width: colWidths.designation }}
                >
                  <span
                    title={row.designation}
                    style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.designation}
                  </span>
                </td>
                <td
                  align="left"
                  style={{ padding: "6px", width: colWidths.city }}
                >
                  <span
                    title={row.city_hq}
                    style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.city_hq}
                  </span>
                </td>

                <td
                  align="left"
                  style={{ padding: "6px", width: colWidths.division }}
                >
                  <span
                    title={row.division}
                    style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.division}
                  </span>
                </td>
                <td
                  align="left"
                  style={{ padding: "6px", width: colWidths.doj }}
                >
                  <span>{formatDate ? formatDate(row.doj) : row.doj}</span>
                </td>

                <td
                  align="left"
                  style={{ padding: "6px", width: colWidths.dor }}
                >
                  <span>{formatDate ? formatDate(row.dor) : row.dor}</span>
                </td>

                <td
                  align="left"
                  style={{ padding: "6px", width: colWidths.manager }}
                >
                  <span
                    title={row.manager}
                    style={{
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.manager || "N/A"}
                  </span>
                </td>
                <td
                  align="center"
                  style={{ padding: "6px", width: colWidths.mobile }}
                >
                  <a
                    onClick={(e) => e.preventDefault()}
                    title={row.mobile || "-"}
                    href="#"
                    className="text-blue-600 underline"
                    style={{
                      fontSize: "13px",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {row.mobile || "-"}
                  </a>
                </td>

                {generateDayCells(row)}

                <td
                  align="center"
                  style={{ padding: "6px", width: colWidths.total }}
                >
                  <span>{row.total ?? row.total_qty ?? 0}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mt-4 flex gap-4 justify-center">
          <CustomButton onClick={handlePrint} label="Print" variant="print">
            🖨
          </CustomButton>
          <CustomButton
            onClick={handleExportExcel}
            label="Export"
            variant="export"
          >
            📥
          </CustomButton>
        </div>
      </div>

      {selectedEmployee && selectedDate && (
        <ProductSalesModal
          employeeId={selectedEmployee}
          date={selectedDate}
          onClose={closeModal}
          customUrl={`${BASE_URL}/api/reports/temptation-report-daily?employee_id=${selectedEmployee}&filled_date=${selectedDate}`}
        />
      )}
    </>
  );
};

export default MonthlyCountCampaign;
